import { Trophy } from 'lucide-react';
import ShareButton from './ShareButton';
import StoryShareButton from './StoryShareButton';
import { buildTeamRowAvatar } from '../lib/avatars';
import { formatFrancAdaptive } from '../lib/utils';
import { lastDayLabel } from './LeagueClosedNote';

/**
 * QuarterRecapCard — 2026-11-01 (owner). A player's FINAL result in a closed
 * league: avatar, team name, "#rank of N players", points, plus a Share
 * button that hands out the server-rendered recap card (same layout).
 */
export const QuarterRecapCard = ({ recap, testid = 'quarter-recap-card', compact = false }) => {
  if (!recap) return null;
  const avatar = buildTeamRowAvatar({ ...recap, name: recap.team_name }, null, 96);
  const day = lastDayLabel(recap.ended_at);
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[hsl(var(--accent)/0.4)] bg-gradient-to-br from-[hsl(var(--accent)/0.14)] via-card to-card p-4"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground" data-testid={`${testid}-label`}>
          {recap.quarter_label} · Final standings
        </p>
        <ShareButton
          path={`/api/share/recap/${recap.team_id}`}
          imagePath={`/api/share/recap/${recap.team_id}/card.png`}
          title={`${recap.team_name} — ${recap.quarter_label} final`}
          text={`I finished #${recap.rank} of ${recap.total} in ${recap.quarter_label} on Fantasy World Star!`}
          iconOnly={compact}
          testid={`${testid}-share-btn`}
        />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <img
          src={avatar}
          alt=""
          className="h-14 w-14 shrink-0 rounded-full border-2 border-[hsl(var(--accent))] bg-secondary object-cover"
          data-testid={`${testid}-avatar`}
        />
        <div className="min-w-0">
          <p className="truncate text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`${testid}-team-name`}>
            {recap.team_name}
          </p>
          {recap.username && <p className="truncate text-xs text-muted-foreground">@{recap.username}</p>}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Final rank</p>
        <p className="tabular-nums leading-none whitespace-nowrap" data-testid={`${testid}-rank`}>
          <span className="text-4xl font-extrabold text-[hsl(var(--accent))]" style={{ fontFamily: 'var(--font-heading)' }}>
            #{Number(recap.rank).toLocaleString()}
          </span>
          <span className="ml-1.5 text-sm text-muted-foreground">of {Number(recap.total).toLocaleString()} players</span>
        </p>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Points</p>
          <p className="text-xl font-bold tabular-nums whitespace-nowrap" data-testid={`${testid}-points`}>
            {Math.round(recap.points || 0).toLocaleString()}
          </p>
        </div>
        <div title="Final standings rank by net worth">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Net worth</p>
          <p className="text-xl font-bold tabular-nums whitespace-nowrap" data-testid={`${testid}-net-worth`}>
            {formatFrancAdaptive(recap.net_worth, { tight: true })}
          </p>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Ranked by net worth.</p>
      {day && (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Trophy className="h-3 w-3 text-[hsl(var(--accent))]" />
          Frozen as it stood on {day}.
        </p>
      )}
      <StoryShareButton
        imagePath={`/api/share/recap/${recap.team_id}/story.png`}
        fileName={`fws-${recap.quarter_label.replace(/\s+/g, '-').toLowerCase()}-story.png`}
        title={`${recap.team_name} — ${recap.quarter_label} final`}
        testid={`${testid}-story-btn`}
      />
    </div>
  );
};

export default QuarterRecapCard;
